import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Cpu,
  Lock,
  Eye,
  Bank,
  Buildings,
  HardHat,
  ArrowRight,
  Lightning,
  Copy,
  Download,
  Camera as CameraIcon,
} from "@phosphor-icons/react";

/* ────────────────────────────────────────────────────────────────────
   /cc/enterprise — Curb Checkpoint pitch page for B2B prospects.
   Public, sharable URL. One-tap live demo.

   Demo flow:
     1. Visitor taps "GENERATE A DEMO DOCUMENT"
     2. Backend issues a 2-hour demo template + returns Share A PNG
     3. Page displays the Share A PNG large + the doc_id
     4. Visitor opens /cc/scan on phone, types doc_id, points at screen
     5. Sees VERIFIED on phone in <300 ms

   Built for a real pitch — text is short, claims are accurate, visuals
   demonstrate before they describe. Same Swiss Brutalist house style
   as /pitch/investors.
   ──────────────────────────────────────────────────────────────────── */

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function StatTile({ value, label, sub }) {
  return (
    <div className="border-2 border-black p-5 bg-white">
      <div className="font-display text-3xl sm:text-4xl leading-none">{value}</div>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase mt-3 text-neutral-700">{label}</div>
      {sub && <div className="text-xs text-neutral-600 mt-2 leading-snug">{sub}</div>}
    </div>
  );
}

function VerticalCard({ icon: Icon, name, useCase, value }) {
  return (
    <div className="bg-black text-white border-2 border-black p-6">
      <Icon size={28} weight="bold" className="text-[#FFCC00]" />
      <div className="font-display text-xl mt-3">{name}</div>
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase mt-2 text-[#FFCC00]">Use case</div>
      <p className="text-sm mt-1 text-neutral-200 leading-relaxed">{useCase}</p>
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase mt-4 text-[#FFCC00]">Value</div>
      <p className="text-sm mt-1 text-neutral-200 leading-relaxed">{value}</p>
    </div>
  );
}

export default function CheckpointEnterprise() {
  const [demoState, setDemoState] = useState("idle"); // idle | loading | ready | error
  const [demo, setDemo] = useState(null);  // { doc_id, share_a_png_b64, secret_text }
  const [errMsg, setErrMsg] = useState("");
  const [copiedField, setCopiedField] = useState("");
  const shareRef = useRef(null);

  async function issueDemo() {
    setDemoState("loading");
    setErrMsg("");
    try {
      const r = await fetch(`${API_BASE}/api/cc/demo/issue`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error(`Server ${r.status}`);
      const body = await r.json();
      setDemo(body);
      setDemoState("ready");
      // Scroll to the demo result so visitor sees the QR + instructions.
      setTimeout(() => shareRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      setDemoState("error");
      setErrMsg(e.message || "Could not issue demo");
    }
  }

  async function copy(text, fieldName) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(""), 1500);
    } catch { /* clipboard unavailable */ }
  }

  function downloadShareA() {
    if (!demo) return;
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${demo.share_a_png_b64}`;
    a.download = `share-a-${demo.doc_id}.png`;
    a.click();
  }

  useEffect(() => {
    document.title = "Curb Checkpoint · Enterprise";
  }, []);

  return (
    <div className="min-h-screen bg-white text-black">
      {/* ───── Hero ───── */}
      <section className="bg-black text-white border-b-2 border-black">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-20 sm:py-28">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#FFCC00]">
            Curb Checkpoint · Enterprise
          </div>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6 max-w-4xl">
            Visual cryptography for documents<br />
            that <span className="bg-[#FFCC00] text-black px-2">cannot be forged.</span>
          </h1>
          <p className="mt-8 text-base sm:text-lg max-w-2xl text-neutral-300 leading-relaxed">
            A printable share carries half the secret. A private share lives only on your server. Together they reveal a code. A forged share reveals a <strong className="text-[#FFCC00]">destructive glitch</strong> — not a polite rejection.
          </p>

          <div className="mt-12 flex flex-wrap gap-3">
            <button
              onClick={issueDemo}
              disabled={demoState === "loading"}
              className="inline-flex items-center gap-2 bg-[#FFCC00] text-black px-6 py-4 font-mono text-sm tracking-[0.3em] uppercase disabled:opacity-50"
              data-testid="cc-enterprise-demo-btn"
            >
              <Lightning size={14} weight="bold" />
              {demoState === "loading" ? "Generating…" : "Try the live demo"}
            </button>
            <Link
              to="/cc/scan"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-4 font-mono text-sm tracking-[0.3em] uppercase"
              data-testid="cc-enterprise-scanner-link"
            >
              <CameraIcon size={14} weight="bold" /> Open scanner
            </Link>
            <Link
              to="/cc/postnet"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-4 font-mono text-sm tracking-[0.3em] uppercase"
              data-testid="cc-enterprise-postnet-link"
            >
              Postnet pilot →
            </Link>
          </div>
        </div>
      </section>

      {/* ───── Live demo result ───── */}
      {demoState === "ready" && demo && (
        <section ref={shareRef} className="bg-[#FFCC00] text-black border-b-2 border-black">
          <div className="max-w-6xl mx-auto px-6 sm:px-12 py-16">
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase">Demo issued</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight">
              Verify this in 30 seconds on your phone.
            </h2>

            <div className="mt-10 grid sm:grid-cols-5 gap-6 items-start">
              {/* Share A image — the "printable" half */}
              <div className="sm:col-span-2 bg-white border-2 border-black p-4">
                <img
                  src={`data:image/png;base64,${demo.share_a_png_b64}`}
                  alt="Share A — printable half of the secret"
                  className="block w-full h-auto"
                  data-testid="cc-enterprise-share-a"
                />
                <div className="mt-3 font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-700">
                  SHARE A · PRINTABLE
                </div>
              </div>

              {/* Instructions + metadata */}
              <div className="sm:col-span-3">
                <ol className="space-y-4 text-sm">
                  <li className="flex gap-3">
                    <span className="font-mono text-base shrink-0">01</span>
                    <span>
                      On your phone, open <code className="font-mono bg-black/10 px-1">curbcrew.co.za/cc/scan</code> in Safari or Chrome.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-base shrink-0">02</span>
                    <span>
                      Enter the document ID:
                      <button
                        onClick={() => copy(demo.doc_id, "doc")}
                        className="ml-2 font-mono text-base bg-black text-[#FFCC00] px-2 py-0.5 inline-flex items-center gap-2"
                        data-testid="cc-enterprise-copy-docid"
                      >
                        {demo.doc_id}
                        <Copy size={12} weight="bold" />
                        {copiedField === "doc" && <span className="text-[10px]">COPIED</span>}
                      </button>
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-base shrink-0">03</span>
                    <span>
                      Tap <strong>BEGIN CAPTURE</strong>, grant camera permission, point at the Share A image on this screen.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-base shrink-0">04</span>
                    <span>
                      In under 300 ms you&apos;ll see <strong>✓ Verified · {demo.secret_text}</strong>. Try with a different image, the screen turns <strong>FRAUD DETECTED</strong>.
                    </span>
                  </li>
                </ol>

                <div className="mt-6 flex gap-3 flex-wrap">
                  <button
                    onClick={downloadShareA}
                    className="inline-flex items-center gap-2 bg-black text-[#FFCC00] px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase"
                    data-testid="cc-enterprise-download-share"
                  >
                    <Download size={12} weight="bold" /> Download Share A
                  </button>
                  <Link
                    to="/cc/scan"
                    className="inline-flex items-center gap-2 bg-black text-[#FFCC00] px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase"
                  >
                    <CameraIcon size={12} weight="bold" /> Open scanner
                  </Link>
                </div>

                <p className="mt-4 text-xs text-neutral-700 leading-relaxed">
                  This demo template is valid for <strong>2 hours</strong>. Production templates can be issued with any lifespan and revoked instantly.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {demoState === "error" && (
        <section className="bg-red-50 border-b-2 border-red-700">
          <div className="max-w-6xl mx-auto px-6 sm:px-12 py-6 text-sm text-red-900">
            Could not generate demo: {errMsg}
          </div>
        </section>
      )}

      {/* ───── How it works ───── */}
      <section className="border-b-2 border-black">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-neutral-500">How it works</div>
          <h2 className="font-display text-3xl sm:text-5xl mt-3 leading-tight max-w-3xl">
            Two halves. One secret. Zero forgeries.
          </h2>

          <ol className="mt-12 space-y-8">
            {[
              { n: "01", t: "Issue a credential.", b: "Operator (you) registers the secret you want each document to carry — an account number, ID, audit code, anything up to 16 chars. We mint two random pixel grids. Together they XOR to the secret. Alone, both are statistical noise." },
              { n: "02", t: "Print Share A on the document.", b: "Goes on the back of a card, the corner of a certificate, the seal on a manila envelope. Anyone can photograph it. They get noise." },
              { n: "03", t: "Operator scans at the checkpoint.", b: "Mobile browser, no app install. Camera aligns the printed share inside a reticle. Frames stream to your private server at 1 Hz." },
              { n: "04", t: "Server stacks Share A + Share B.", b: "XOR-overlay. If the document is genuine and aligned, the secret resolves. We hash it and compare to the value we issued." },
              { n: "05", t: "Pass — secret revealed. Fail — banner burns.", b: "Match: phone displays the secret in green. Mismatch: a black FRAUD DETECTED banner overlays the document, with the destructive artifact visibly bleeding through. Both responses inside 300 ms." },
            ].map((s) => (
              <li key={s.n} className="flex gap-5">
                <div className="font-mono text-2xl text-neutral-300 shrink-0">{s.n}</div>
                <div>
                  <div className="font-display text-lg sm:text-xl">{s.t}</div>
                  <p className="text-sm text-neutral-700 leading-relaxed mt-1 max-w-3xl">{s.b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ───── Verticals ───── */}
      <section className="bg-black text-white border-b-2 border-black">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#FFCC00]">Where it earns its keep</div>
          <h2 className="font-display text-3xl sm:text-5xl mt-3 leading-tight max-w-3xl">
            High-value documents,<br />
            in three industries first.
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-4">
            <VerticalCard
              icon={Bank}
              name="Banking & FinServ"
              useCase="Branch-issued letters, bond approval docs, KYC certificates — every page carries a Share A. Customer fraud rings can&apos;t fake a real branch letter."
              value="Insurance-recoverable fraud loss. Per-document license: R3–8 / issue, capped at R0.50 / verify."
            />
            <VerticalCard
              icon={Buildings}
              name="Government & Permits"
              useCase="Police clearances, building permits, vehicle disc renewals, ID copy verification at airports / borders. Operator at the checkpoint, no expensive scanner hardware."
              value="Tender-grade compliance. Per-document pricing R5–15 / issue."
            />
            <VerticalCard
              icon={HardHat}
              name="Mining & Logistics"
              useCase="Driver permits, chain-of-custody seals on high-value cargo (gold, platinum, electronics), site-access credentials at boom gates."
              value="Per-shipment Share A printed on the seal. R8–20 / seal. Volumes are large."
            />
          </div>
        </div>
      </section>

      {/* ───── Why this not OCR / hologram / NFC ───── */}
      <section className="border-b-2 border-black">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-neutral-500">Why visual cryptography</div>
          <h2 className="font-display text-3xl sm:text-5xl mt-3 leading-tight max-w-3xl">
            Cheaper than holograms.<br />
            Harder to fake than OCR.<br />
            Works without NFC hardware.
          </h2>

          <div className="mt-12 overflow-x-auto border-2 border-black">
            <table className="w-full text-sm">
              <thead className="bg-black text-white">
                <tr>
                  <th className="text-left p-3 font-mono text-[10px] tracking-widest uppercase">Approach</th>
                  <th className="text-center p-3 font-mono text-[10px] tracking-widest uppercase">Cost / doc</th>
                  <th className="text-center p-3 font-mono text-[10px] tracking-widest uppercase">Phone-only verify</th>
                  <th className="text-center p-3 font-mono text-[10px] tracking-widest uppercase">Forgery-proof</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-neutral-300">
                  <td className="p-3 font-bold">Hologram sticker</td>
                  <td className="p-3 text-center">R12–80</td>
                  <td className="p-3 text-center">—</td>
                  <td className="p-3 text-center">Medium (counterfeit market exists)</td>
                </tr>
                <tr className="border-t border-neutral-300">
                  <td className="p-3 font-bold">UV/IR ink</td>
                  <td className="p-3 text-center">R5–25</td>
                  <td className="p-3 text-center">—</td>
                  <td className="p-3 text-center">High (but needs specialist hardware)</td>
                </tr>
                <tr className="border-t border-neutral-300">
                  <td className="p-3 font-bold">OCR + database lookup</td>
                  <td className="p-3 text-center">R0.30</td>
                  <td className="p-3 text-center">✓</td>
                  <td className="p-3 text-center">Low (can be forged with a printer)</td>
                </tr>
                <tr className="border-t border-neutral-300">
                  <td className="p-3 font-bold">NFC chip</td>
                  <td className="p-3 text-center">R20–60</td>
                  <td className="p-3 text-center">Partial (iOS limited)</td>
                  <td className="p-3 text-center">High (but hardware cost)</td>
                </tr>
                <tr className="border-t-2 border-black bg-[#FFCC00]">
                  <td className="p-3 font-display text-base">Curb Checkpoint</td>
                  <td className="p-3 text-center font-bold">R0.50–R20</td>
                  <td className="p-3 text-center font-bold">✓</td>
                  <td className="p-3 text-center font-bold">High (server-side secret)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ───── Trust signals ───── */}
      <section className="border-b-2 border-black">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-neutral-500">Engineering</div>
          <h2 className="font-display text-3xl sm:text-5xl mt-3 leading-tight">
            Built like infrastructure.
          </h2>
          <div className="mt-12 grid sm:grid-cols-4 gap-4">
            <StatTile value="<300ms" label="Scan → result" sub="Median round-trip on 4G, ≤ 1 s at the 95th percentile." />
            <StatTile value="64×64" label="Share resolution" sub="Each bit is 8×8 px after upscale — robust to camera noise." />
            <StatTile value="0 apps" label="Operator install" sub="Pure browser. WebRTC + Fetch. iOS Safari 14+, Android Chrome 90+." />
            <StatTile value="HMAC-SHA256" label="Secret binding" sub="Each share pair is bound to a server-only key. Leaked shares are useless." />
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <div className="border-2 border-black p-5 flex items-start gap-3">
              <ShieldCheck size={22} weight="bold" />
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase">Audit trail</div>
                <p className="text-xs mt-1 text-neutral-700">Every verify event logged. POPIA / GDPR-aligned retention controls.</p>
              </div>
            </div>
            <div className="border-2 border-black p-5 flex items-start gap-3">
              <Lock size={22} weight="bold" />
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase">Revocation</div>
                <p className="text-xs mt-1 text-neutral-700">Compromised document? One flag flip kills its share globally.</p>
              </div>
            </div>
            <div className="border-2 border-black p-5 flex items-start gap-3">
              <Eye size={22} weight="bold" />
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase">No image upload retention</div>
                <p className="text-xs mt-1 text-neutral-700">Captured frames are verified then dropped. No photo lake.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── CTA ───── */}
      <section className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#FFCC00]">Pilot</div>
          <h2 className="font-display text-3xl sm:text-5xl mt-3 leading-tight">
            14-day pilot, 500 documents,<br />
            no contract.
          </h2>
          <p className="mt-4 text-sm text-neutral-300 max-w-2xl">
            We issue 500 share pairs against a doc class you nominate. Your operators verify in the field. At day 14 we hand over the audit log. Pilot fee R12 000 inc VAT. Convert to per-document pricing on month two.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="mailto:billy@hadlowinnovations.co.za?subject=Checkpoint%20Charlie%20pilot"
              className="inline-flex items-center gap-2 bg-[#FFCC00] text-black px-6 py-4 font-mono text-sm tracking-[0.3em] uppercase"
              data-testid="cc-enterprise-contact-btn"
            >
              Book a pilot <ArrowRight size={14} weight="bold" />
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-4 font-mono text-sm tracking-[0.3em] uppercase"
            >
              <Cpu size={14} weight="bold" /> See the Curb stack
            </Link>
          </div>

          <div className="mt-16 pt-6 border-t border-white/20 text-xs text-white/50">
            Curb Checkpoint · A Curb Crew capability · curbcrew.co.za
          </div>
        </div>
      </section>
    </div>
  );
}
